// components/Modals/BannerModal/BannerModal.js
import React, { useState, useEffect } from 'react';
import './BannerModal.css';
import { toast } from 'react-hot-toast';
import { API_BASE_URL_EXPORT } from '../../../config';

const POSITION_LABELS = {
  top_1: 'Üst Banner 1 (Player üstü sol)',
  top_2: 'Üst Banner 2 (Player üstü sağ)',
  bottom_1: 'Alt Banner 1 (Player altı sol)',
  bottom_2: 'Alt Banner 2 (Player altı sağ)',
  sidebar: 'Sidebar Banner',
  background: 'Sayfa Arka Plan Görseli'
};

const BannerModal = ({ isOpen, onClose, onSubmit, editingBanner, API_URL }) => {
  const [formData, setFormData] = useState({
    title: '',
    link: '',
    position: 'top_1',
    is_active: 1,
    sort_order: 0,
    image: null
  });
  const [previewImage, setPreviewImage] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (editingBanner) {
      setFormData({
        title: editingBanner.title || '',
        link: editingBanner.link || '',
        position: editingBanner.position || 'top_1',
        is_active: editingBanner.is_active !== undefined ? editingBanner.is_active : 1,
        sort_order: editingBanner.sort_order || 0,
        image: null
      });
      if (editingBanner.image) {
        setPreviewImage(`${API_BASE_URL_EXPORT}/uploads/${editingBanner.image}`);
      }
    } else {
      resetForm();
    }
  }, [editingBanner, API_URL]);

  const resetForm = () => {
    setFormData({
      title: '',
      link: '',
      position: 'top_1',
      is_active: 1,
      sort_order: 0,
      image: null
    });
    setPreviewImage(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        toast.error('Sadece resim dosyaları yüklenebilir!');
        return;
      }
      if (file.size > 15 * 1024 * 1024) {
        toast.error('Dosya boyutu 15MB\'dan küçük olmalıdır!');
        return;
      }
      setFormData(prev => ({
        ...prev,
        image: file
      }));
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewImage(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveImage = () => {
    setFormData(prev => ({
      ...prev,
      image: null
    }));
    setPreviewImage(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.title.trim()) {
      toast.error('Başlık zorunludur!');
      return;
    }

    if (!editingBanner && !formData.image) {
      toast.error('Görsel zorunludur!');
      return;
    }

    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      const submitData = new FormData();

      submitData.append('title', formData.title.trim());
      submitData.append('link', formData.link.trim());
      submitData.append('position', formData.position);
      submitData.append('is_active', formData.is_active);
      submitData.append('sort_order', formData.sort_order);

      if (formData.image) {
        submitData.append('image', formData.image);
      }

      const url = editingBanner
        ? `${API_URL}/banners/${editingBanner.id}`
        : `${API_URL}/banners`;

      const method = editingBanner ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method: method,
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: submitData
      });

      const data = await response.json();

      if (data.success) {
        toast.success(editingBanner ? 'Banner güncellendi!' : 'Banner eklendi!');
        onSubmit();
        resetForm();
      } else {
        toast.error(data.error || 'İşlem başarısız');
      }
    } catch (error) {
      console.error('Hata:', error);
      toast.error('Bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content banner-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{editingBanner ? 'Banner Düzenle' : 'Yeni Banner Ekle'}</h3>
          <button className="modal-close" onClick={onClose}>&#10005;</button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="form-group">
              <label htmlFor="banner-title">Başlık <span className="required">*</span></label>
              <input
                type="text"
                id="banner-title"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                placeholder="Banner açıklaması (admin için)"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="banner-link">Tıklama Linki</label>
              <input
                type="url"
                id="banner-link"
                name="link"
                value={formData.link}
                onChange={handleInputChange}
                placeholder="https://example.com"
              />
            </div>

            <div className="form-row">
              <div className="form-group form-group-half">
                <label htmlFor="banner-position">Pozisyon <span className="required">*</span></label>
                <select
                  id="banner-position"
                  name="position"
                  value={formData.position}
                  onChange={handleInputChange}
                  className="form-select"
                >
                  {Object.entries(POSITION_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </select>
              </div>

              <div className="form-group form-group-half">
                <label htmlFor="banner-sort">Sıralama</label>
                <input
                  type="number"
                  id="banner-sort"
                  name="sort_order"
                  value={formData.sort_order}
                  onChange={handleInputChange}
                  min="0"
                  placeholder="0"
                />
              </div>
            </div>

            <div className="form-group">
              <label>Durum</label>
              <div className="toggle-group">
                <button
                  type="button"
                  className={`toggle-btn ${formData.is_active == 1 ? 'active' : ''}`}
                  onClick={() => setFormData(prev => ({ ...prev, is_active: 1 }))}
                >
                  Aktif
                </button>
                <button
                  type="button"
                  className={`toggle-btn ${formData.is_active == 0 ? 'active inactive' : ''}`}
                  onClick={() => setFormData(prev => ({ ...prev, is_active: 0 }))}
                >
                  Pasif
                </button>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="banner-image">
                Banner Görseli {!editingBanner && <span className="required">*</span>}
              </label>
              <p className="form-hint">GIF, PNG veya JPG. Maksimum 15MB.</p>

              {previewImage && (
                <div className="image-preview">
                  <img src={previewImage} alt="Preview" />
                  <button type="button" onClick={handleRemoveImage} className="remove-image-btn">&#10005; Görseli Kaldır</button>
                </div>
              )}

              <input
                type="file"
                id="banner-image"
                accept="image/*"
                onChange={handleImageChange}
                style={{ display: 'none' }}
              />

              {!previewImage && (
                <label htmlFor="banner-image" className="file-upload-label">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                    <circle cx="8.5" cy="8.5" r="1.5"/>
                    <polyline points="21 15 16 10 5 21"/>
                  </svg>
                  <span>Banner Görseli Seç</span>
                </label>
              )}
            </div>
          </div>

          <div className="modal-footer">
            <button type="button" onClick={onClose} className="btn-secondary" disabled={loading}>İptal</button>
            <button type="submit" className="btn-primary" disabled={loading}>
              {loading ? 'Kaydediliyor...' : (editingBanner ? 'Güncelle' : 'Ekle')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BannerModal;